// @ts-check
// Número grande com variação e meta, tabela com totais, texto formatado e página incorporada dos widgets do painel.
// O número, o texto secundário que é um conjunto, as células numéricas e o rodapé "Ver todos" abrem a lista filtrada
// (contrato 07, seção 10); a meta, a variação e a ocupação das turmas não são conjuntos de registros.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { RichTextView } from '../record-show/rich-text-editor.js';
import { IFRAME_SANDBOX } from '../../../../shared/dashboards/iframe-url.js';
import { checkIframeUrlForThisApp } from './iframe-check.js';
import { Tag } from '../../ui/chip.js';
import { navigate } from '../../app/router.js';
import { useElementSize } from './chart-utils.js';
import { formatNumber } from '../../lib/format.js';
import { computeVariation, formatCellValue, formatMetricValue } from './widget-format.js';
import { DrillLink, useDrilldown } from './drilldown.js';
import { DRILLDOWN_EMPTY_ROW_ID, DRILLDOWN_TOTAL_ROW_ID, REPORT_ROW_PRIMARY_COLUMN } from '../../../../shared/dashboards/drilldown-points.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/dashboards.js').NumberWidgetData} NumberWidgetData
 * @typedef {import('../../data/dashboards.js').TableWidgetData} TableWidgetData
 */

/** Situações conhecidas das tabelas educacionais e a cor da etiqueta. */
const STATUS_COLORS = {
  LOTADA: 'red',
  QUASE_LOTADA: 'orange',
  ABAIXO_DO_MINIMO: 'amber',
  DISPONIVEL: 'green',
  OK: 'green',
};

/**
 * @param {{ data: NumberWidgetData, prefix?: string, suffix?: string, lowerIsBetter?: boolean, compact?: boolean }} props
 */
export function NumberWidget({ data, prefix = '', suffix = '', lowerIsBetter = false, compact = false }) {
  const variation = computeVariation(data.value, data.previous);
  const hasGoal = data.goal !== null && data.goal !== undefined && Number(data.goal) > 0;
  // a meta é da campanha: compara o acumulado desde o início das matrículas, não só o período do filtro
  const goalProgress = data.goalProgress ?? data.value ?? 0;
  const goalShare = hasGoal ? (Number(goalProgress) / Number(data.goal)) * 100 : 0;
  const isImprovement = variation !== null && variation !== 0 && (variation > 0) !== lowerIsBetter;
  const trend = variation === null ? null : variation === 0 ? 'flat' : isImprovement ? 'better' : 'worse';
  const secondaryValue = data.secondary?.value;
  const [containerRef, size] = useElementSize();
  const drill = useDrilldown();
  const valueText = `${prefix}${formatMetricValue(data.value, data.format)}${suffix}`;
  // valores longos (moeda) diminuem até 16 px antes de cortar, para caber em cartões de 2 ou 3 colunas
  const fontSize = size.width > 0 ? Math.max(16, Math.min(24, size.width / (valueText.length * 0.62))) : 24;
  const secondaryText = typeof secondaryValue === 'number' ? formatMetricValue(secondaryValue, 'NUMBER') : secondaryValue;
  const valueTarget = drill?.target({ part: 'value' });
  // na conversão o texto "X de Y" abre a base (os Y novos alunos), não os X matriculados: o link diz isso
  const secondaryCount = typeof data.secondary?.count === 'number' ? data.secondary.count : null;
  const isRateBase = drill?.report === 'CONVERSION_RATE' && secondaryCount !== null;
  const secondaryLinkLabel = isRateBase ? t('dashboardDrilldown.conversionBase') : data.secondary?.label ?? '';
  const secondaryLinkValue = isRateBase ? formatMetricValue(secondaryCount, 'NUMBER') : String(secondaryText);
  const secondaryHint = isRateBase ? t('dashboardDrilldown.conversionBaseHint', { count: secondaryCount }) : undefined;
  return html`<div class="number-widget" data-compact=${compact ? 'true' : undefined} ref=${containerRef}>
    <div class="number-widget-main">
      ${valueTarget
        ? html`<${DrillLink} point=${{ part: 'value' }} label=${drill?.widgetTitle ?? ''} valueText=${valueText} className="number-widget-value">
            <span style=${{ fontSize: `${fontSize}px` }}>${valueText}</span>
          <//>`
        : html`<span class="number-widget-value" title=${valueText} style=${{ fontSize: `${fontSize}px` }}>${valueText}</span>`}
      ${trend
        ? html`<span class="number-widget-trend" data-trend=${trend}
            title=${t('dashboards.number.previousTooltip', { value: formatMetricValue(data.previous, data.format) })}>
            <${Icon} name=${variation === 0 ? 'IconMinus' : variation > 0 ? 'IconTrendingUp' : 'IconTrendingDown'} size=${14} />
            ${variation > 0 ? '+' : ''}${formatNumber(variation, { percentage: true, decimals: Math.abs(variation) < 10 ? 1 : 0 })}
          </span>`
        : null}
    </div>
    ${data.previous !== null && data.previous !== undefined && !compact
      ? html`<div class="number-widget-caption">${t('dashboards.number.previous', { value: formatMetricValue(data.previous, data.format) })}</div>`
      : null}
    ${hasGoal
      ? html`<div class="number-widget-goal">
          <div class="number-widget-goal-bar" role="progressbar" aria-valuenow=${Math.round(goalShare)} aria-valuemin="0" aria-valuemax="100">
            <span style=${{ width: `${Math.min(100, goalShare)}%` }} data-complete=${goalShare >= 100 ? 'true' : undefined}></span>
          </div>
          <span class="number-widget-goal-text" title=${t('dashboards.number.goalTooltip', { share: formatNumber(goalShare, { percentage: true, decimals: 0 }) })}>${data.goalSince
            ? t('dashboards.number.goalSince', {
              progress: formatMetricValue(goalProgress, data.format),
              goal: formatMetricValue(data.goal, data.format),
              since: `${data.goalSince.slice(8, 10)}/${data.goalSince.slice(5, 7)}/${data.goalSince.slice(0, 4)}`,
            })
            : t('dashboards.number.goal', { share: formatNumber(goalShare, { percentage: true, decimals: 0 }), goal: formatMetricValue(data.goal, data.format) })}</span>
        </div>`
      : null}
    ${data.secondary && secondaryValue !== null && secondaryValue !== undefined && secondaryValue !== ''
      ? html`<div class="number-widget-secondary">
          <span>${data.secondary.label}</span>
          <strong><${DrillLink} point=${{ part: 'secondary' }} label=${secondaryLinkLabel} valueText=${secondaryLinkValue} hint=${secondaryHint}>${secondaryText}<//></strong>
        </div>`
      : null}
  </div>`;
}

/**
 * @param {string} value
 */
export function getStatusLabel(value) {
  const key = `dashboards.status.${value}`;
  return hasTranslation(key) ? t(key) : value;
}

/**
 * @param {{ value: any, format?: string, options?: { value: string, label: string, color?: string }[] }} props
 */
function TableCell({ value, format, options }) {
  if (format === 'STATUS') {
    if (value === null || value === undefined || value === '') return null;
    // as opções vindas da API mandam no rótulo e na cor; as conhecidas cobrem respostas sem opções
    const option = options?.find((candidate) => candidate.value === String(value));
    return html`<${Tag} text=${option?.label ?? getStatusLabel(String(value))} color=${option?.color ?? STATUS_COLORS[String(value)] ?? 'gray'} />`;
  }
  return formatCellValue(value, /** @type {any} */ (format));
}

/**
 * Rodapé "Ver todos os N" da tabela genérica e da agenda: a tabela mostra só as primeiras linhas. Na agenda o número
 * conta eventos e a lista abre cada card uma vez: o texto diz os dois ("Ver os 21 cards (22 eventos)").
 * @param {{ totalCount: number, distinctCount?: number | null }} props
 */
function TableSeeAll({ totalCount, distinctCount = null }) {
  const drill = useDrilldown();
  if (!drill?.target({ part: 'all' })) return null;
  const hasPairs = typeof distinctCount === 'number' && distinctCount > 0 && distinctCount < totalCount;
  const text = hasPairs
    ? t('dashboardDrilldown.seeAllCards', { count: /** @type {number} */ (distinctCount), events: formatMetricValue(totalCount, 'NUMBER') })
    : t('dashboardDrilldown.seeAllCount', { count: totalCount });
  const valueText = hasPairs
    ? t('dashboardDrilldown.cardsOfEvents', { count: /** @type {number} */ (distinctCount), events: formatMetricValue(totalCount, 'NUMBER') })
    : formatMetricValue(totalCount, 'NUMBER');
  return html`<div class="table-widget-see-all">
    <${DrillLink} point=${{ part: 'all' }} label=${drill.widgetTitle} valueText=${valueText} className="table-widget-see-all-link">
      <span>${text}</span><${Icon} name="IconArrowUpRight" size=${14} />
    <//>
  </div>`;
}

/**
 * @param {{ data: TableWidgetData, variant?: 'report' }} props
 */
export function TableWidget({ data, variant }) {
  const drill = useDrilldown();
  const isNumeric = (/** @type {string | undefined} */ format) => format === 'NUMBER' || format === 'CURRENCY' || format === 'PERCENT';
  const report = drill?.report ?? null;
  // tabelas de indicador: cada célula numérica abre a lista dela; o rótulo da linha sem link abre a coluna principal
  const isIndicatorTable = drill?.widgetType === 'KPI';
  const primaryColumn = report ? /** @type {Record<string, string>} */ (REPORT_ROW_PRIMARY_COLUMN)[report] ?? null : null;
  const labelColumnKey = data.columns[0]?.key;
  const totalCount = /** @type {any} */ (data).totalCount;
  const distinctCount = /** @type {any} */ (data).distinctCount;
  /**
   * @param {string} rowId @param {string} rowLabel @param {{ key: string, label: string, format?: string }} column @param {any} value @param {any} content
   * @param {number | undefined} [linkedCount] ocupação das turmas: matrículas vinculadas da célula, o que a lista abre
   */
  const cellLink = (rowId, rowLabel, column, value, content, linkedCount) => {
    if (!isIndicatorTable || !isNumeric(column.format) || typeof value !== 'number' || value <= 0) return content;
    const point = { part: 'cell', rowId, columnKey: column.key };
    const label = `${rowLabel} · ${column.label}`;
    // o número do ERP é maior que as matrículas vinculadas: o número fica sem link e "ver N vinculadas" abre a lista
    if (typeof linkedCount === 'number' && linkedCount < value) {
      if (linkedCount <= 0) return content;
      return html`<span class="table-widget-cell-split">${content}<span class="table-widget-cell-separator" aria-hidden="true">·</span><${DrillLink}
        point=${point} label=${label} valueText=${t('dashboardDrilldown.linkedEnrollments', { count: linkedCount })}
        hint=${t('dashboardDrilldown.linkedHint')} className="table-widget-cell-link table-widget-linked-link"
      >${t('dashboardDrilldown.seeLinked', { count: linkedCount })}<//></span>`;
    }
    return html`<${DrillLink} point=${point} label=${label}
      valueText=${formatCellValue(value, /** @type {any} */ (column.format))} className="table-widget-cell-link">${content}<//>`;
  };
  /** @param {Record<string, number> | undefined} linked @param {string} columnKey */
  const linkedCountOf = (linked, columnKey) => (report === 'CLASS_OCCUPANCY' && linked ? linked[columnKey] : undefined);
  return html`<div class="table-widget" data-variant=${variant}>
    <table>
      <thead>
        <tr>${data.columns.map((column) => html`<th key=${column.key} data-numeric=${isNumeric(column.format) ? 'true' : undefined}>${column.label}</th>`)}</tr>
      </thead>
      <tbody>
        ${data.rows.map((row, index) => {
          const rowId = row.id ?? DRILLDOWN_EMPTY_ROW_ID;
          const rowLabel = String(row.cells?.[labelColumnKey] ?? '');
          const primaryValue = primaryColumn ? row.cells?.[primaryColumn] : null;
          return html`<tr
            key=${row.id ?? index}
            data-clickable=${row.href ? 'true' : undefined}
            tabindex=${row.href ? 0 : undefined}
            onClick=${row.href ? () => navigate(/** @type {string} */ (row.href)) : undefined}
            onKeyDown=${row.href ? (/** @type {KeyboardEvent} */ event) => event.target === event.currentTarget && event.key === 'Enter' && navigate(/** @type {string} */ (row.href)) : undefined}
          >
            ${data.columns.map((column) => {
              const value = row.cells?.[column.key];
              const content = html`<${TableCell} value=${value} format=${column.format} options=${column.options} />`;
              const isRowLabel = column.key === labelColumnKey && !row.href && isIndicatorTable && primaryColumn && typeof primaryValue === 'number' && primaryValue > 0;
              return html`<td key=${column.key} data-numeric=${isNumeric(column.format) ? 'true' : undefined}>
                ${isRowLabel
                  ? html`<${DrillLink} point=${{ part: 'cell', rowId, columnKey: /** @type {string} */ (primaryColumn) }} label=${rowLabel}
                      valueText=${formatMetricValue(/** @type {number} */ (primaryValue), 'NUMBER')} className="table-widget-cell-link">${content}<//>`
                  : cellLink(rowId, rowLabel, column, value, content, linkedCountOf(row.linked, column.key))}
              </td>`;
            })}
          </tr>`;
        })}
      </tbody>
      ${data.totals
        ? html`<tfoot>
            <tr>
              ${data.columns.map((column, index) => html`<td key=${column.key} data-numeric=${isNumeric(column.format) ? 'true' : undefined}>
                ${index === 0 && (data.totals?.[column.key] === undefined || data.totals?.[column.key] === null)
                  ? t('dashboards.chart.total')
                  : column.format === 'STATUS' ? '' : cellLink(DRILLDOWN_TOTAL_ROW_ID, t('dashboards.chart.total'), column, data.totals?.[column.key],
                    formatCellValue(data.totals?.[column.key], /** @type {any} */ (column.format)), linkedCountOf(data.linkedTotals, column.key))}
              </td>`)}
            </tr>
          </tfoot>`
        : null}
    </table>
    ${typeof totalCount === 'number' && totalCount > 0 ? html`<${TableSeeAll} totalCount=${totalCount} distinctCount=${distinctCount} />` : null}
  </div>`;
}

/**
 * Texto formatado do widget TEXT (paridade com o rich text do Twenty): títulos, listas, lista de tarefas, citação,
 * negrito, itálico, código e links, pelo mesmo conversor seguro das notas (todo texto escapado, só links http, https
 * e mailto).
 * @param {{ markdown: string }} props
 */
export function TextWidget({ markdown }) {
  return html`<div class="text-widget"><${RichTextView} markdown=${String(markdown ?? '')} /></div>`;
}

/**
 * Página incorporada (doc 16, seção 6.4; contrato 07, seção 8.2): quadro com sandbox, sem enviar o endereço do CRM
 * como referência. Em edição o quadro não recebe cliques, para o cartão poder ser selecionado e arrastado.
 * @param {{ url: string | null, title: string, isEditing?: boolean }} props
 */
export function IframeWidget({ url, title, isEditing = false }) {
  const check = checkIframeUrlForThisApp(url);
  const [loadedUrl, setLoadedUrl] = useState(/** @type {string | null} */ (null));
  if (check.isValid === false) {
    return html`<div class="widget-message" data-tone="danger">
      <${Icon} name="IconFrame" size=${16} />
      <span>${t(`dashboardParity.iframe.reasons.${check.reason}`)}</span>
    </div>`;
  }
  return html`<div class="iframe-widget" data-editing=${isEditing ? 'true' : undefined}>
    ${loadedUrl !== check.url ? html`<div class="iframe-widget-loading" aria-hidden="true"><${Skeleton} width=${160} height=${14} /></div>` : null}
    <iframe
      key=${check.url}
      src=${check.url}
      title=${title || t('dashboardParity.iframe.defaultTitle')}
      sandbox=${IFRAME_SANDBOX}
      allow="fullscreen; encrypted-media"
      allowFullScreen=${true}
      referrerpolicy="no-referrer"
      loading="lazy"
      onLoad=${() => setLoadedUrl(check.url)}
    ></iframe>
  </div>`;
}
